import React, { useState, useEffect, useCallback } from 'react';
import '../styles/Pipeline.css';
import RelationshipGraph from '../components/RelationshipGraph';

import { useAuth } from '../components/AuthContext';
const API_BASE = import.meta.env.VITE_API_BASE;

/* ── SVG Icons ── */
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M4.5 7l1.8 1.8L9.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPlay = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M5.5 4.5l4 2.5-4 2.5z" fill="currentColor"/>
  </svg>
);
const IconShield = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5l4.5 2v3.5c0 2.8-2 5-4.5 5.5C4.5 12 2.5 9.8 2.5 7V3.5L7 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M5 7l1.5 1.5L9 5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconFolder = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 3.5A1.5 1.5 0 013.5 2h2.3l1.2 1.5h3.5A1.5 1.5 0 0112 5v5.5a1.5 1.5 0 01-1.5 1.5h-7A1.5 1.5 0 012 10.5V3.5z" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const DOT_COLORS = ['#6366f1','#0f766e','#b45309','#475569','#e11d48','#0284c7','#7c3aed','#059669'];

const STEPS = [
  { key: 'select',   label: 'Select Config', icon: IconFolder },
  { key: 'validate', label: 'Validate',      icon: IconCheck },
  { key: 'generate', label: 'Generate',      icon: IconPlay },
  { key: 'confirm',  label: 'Data Check',    icon: IconShield },
];

/* ── Expandable check row for validation results ── */
function DataCheckRow({ name, check }) {
  const [open, setOpen] = useState(false);
  const hasErrors = check.errors?.length > 0;
  const hasDetails = !!check.details;
  const expandable = hasErrors || hasDetails;

  return (
    <div className={`pl-check-row-wrap ${!check.passed ? 'failed' : ''}`}>
      <div
        className={`pl-check-row ${expandable ? 'expandable' : ''}`}
        onClick={() => expandable && setOpen(o => !o)}
      >
        <span className={`pl-check-badge ${check.passed ? 'pass' : 'fail'}`}>
          {check.passed ? '✓' : '✗'}
        </span>
        <span className="pl-check-name">{name.replace(/_/g, ' ')}</span>
        {hasErrors && (
          <span className="pl-check-err-count">
            {check.errors.length} error{check.errors.length !== 1 ? 's' : ''}
          </span>
        )}
        {expandable && (
          <span className={`pl-check-chevron ${open ? 'open' : ''}`}>▸</span>
        )}
      </div>

      {open && hasErrors && (
        <div className="pl-check-errors">
          {check.errors.slice(0, 50).map((e, i) => (
            <div key={i} className="pl-check-error-line">• {e}</div>
          ))}
          {check.errors.length > 50 && (
            <div className="pl-check-error-more">… and {check.errors.length - 50} more</div>
          )}
        </div>
      )}

      {open && hasDetails && (
        <div className="pl-check-details">
          <table className="pl-check-table">
            <thead><tr><th>Entity</th><th>Expected</th><th>Actual</th></tr></thead>
            <tbody>
              {Object.entries(check.details).map(([ent, c]) => (
                <tr key={ent} className={c.expected !== c.actual ? 'mismatch' : ''}>
                  <td>{ent}</td><td>{c.expected}</td><td>{c.actual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Pipeline({ onNavigate }) {
  const { apiFetch, workspace, user } = useAuth();
  /* ── state ── */
  const [configs, setConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [step, setStep] = useState(0);                // 0-3

  // Validate
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  // Generate
  const [genConfig, setGenConfig] = useState({
    output_format: 'csv', seed: 42, validate_ri: true,
    enable_ai: false, ai_provider: 'openai', parallel: true,
    dataset_name: '',
  });
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);

  // Load default AI provider from settings
  useEffect(() => {
    apiFetch(`${API_BASE}/settings`).then(r => r.ok ? r.json() : null).then(data => {
      if (data?.default_provider) {
        setGenConfig(prev => ({ ...prev, ai_provider: data.default_provider }));
      }
    }).catch(() => {});
  }, []);
  const [genStatus, setGenStatus] = useState(null);
  const [entityCount, setEntityCount] = useState(0);

  // Data check
  const [dataValidation, setDataValidation] = useState(null);
  const [dataValidating, setDataValidating] = useState(false);

  // Output preview
  const [outputPreviews, setOutputPreviews] = useState([]);  // [{name, headers, rows}]
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTab, setPreviewTab] = useState(0);
  const [outputFolders, setOutputFolders] = useState([]); // All datasets
  // Load all output folders (datasets) on mount
  useEffect(() => {
    apiFetch(`${API_BASE}/outputs/browse`).then(r => r.ok ? r.json() : null).then(data => {
      setOutputFolders(data?.folders || []);
    }).catch(() => {});
  }, [workspace?.id]);

  const loadOutputPreviews = async (jid) => {
    if (!jid) return;
    setPreviewLoading(true);
    try {
      const listRes = await apiFetch(`${API_BASE}/outputs/${jid}`);
      if (!listRes.ok) return;
      const { files } = await listRes.json();
      const csvFiles = files.filter(f => f.filename.endsWith('.csv') && !f.filename.startsWith('_'));
      const parseCSVLine = (line) => {
        const result = [];
        let cur = ''; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { inQ = !inQ; }
          else if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; }
          else { cur += ch; }
        }
        result.push(cur.trim());
        return result;
      };
      const previews = await Promise.all(csvFiles.map(async (f) => {
        const pRes = await apiFetch(`${API_BASE}/outputs/${jid}/${f.filename}/preview?lines=6`);
        if (!pRes.ok) return null;
        const { data } = await pRes.json();
        if (!data || data.length < 2) return null;
        const headers = parseCSVLine(data[0]);
        const rows = data.slice(1).map(line => parseCSVLine(line));
        return { name: f.filename.replace('.csv', ''), headers, rows };
      }));
      setOutputPreviews(previews.filter(Boolean));
      setPreviewTab(0);
    } catch (e) {
      console.error('Preview load failed:', e);
    }
    setPreviewLoading(false);
  };

  /* ── load configs from backend ── */
  useEffect(() => {
    apiFetch(`${API_BASE}/configs`).then(r => r.json()).then(d => setConfigs(d.configs || [])).catch(() => {});
  }, [workspace?.id]);

  /* ── select a config ── */
  const selectConfig = async (cfg) => {
    setSelectedConfig(cfg);
    setStep(0);
    setValidation(null);
    setGenStatus(null);
    setJobId(null);
    setDataValidation(null);
    setGenConfig(prev => ({ ...prev, dataset_name: cfg.name }));
    try {
      const res = await apiFetch(`${API_BASE}/configs/${encodeURIComponent(cfg.name)}`);
      if (res.ok) setConfigData(await res.json());
    } catch { setConfigData(null); }
  };

  /* ── Step 1: Validate ── */
  const runValidation = async () => {
    if (!configData) return;
    setValidating(true);
    setValidation(null);
    try {
      const res = await apiFetch(`${API_BASE}/rules/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entities: configData.entities }),
      });
      const data = await res.json();
      setValidation(data);
      if (data.valid) setStep(2);
    } catch (e) {
      setValidation({ valid: false, errors: [e.message], warnings: [], entity_count: 0, relationship_count: 0, total_records: 0 });
    }
    setValidating(false);
  };

  /* ── Step 2: Generate ── */
  const startGeneration = async () => {
    if (!configData) return;
    if (!genConfig.dataset_name || !genConfig.dataset_name.trim()) {
      setGenStatus({ status: 'failed', error: 'Dataset name is required.', progress: 0, message: 'Please enter a dataset name before generating.' });
      return;
    }
    setGenerating(true);
    setGenStatus({ status: 'initializing', progress: 0, message: 'Initializing generation...', entity_name: null, generated_records: 0, total_records: null });
    try {
      const res = await apiFetch(`${API_BASE}/generate/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config_name: selectedConfig.name,
          ...genConfig,
          ...(configData ? { config_data: { entities: configData.entities } } : {})
        }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      setJobId(data.job_id);
    } catch (e) {
      setGenStatus({ status: 'failed', error: e.message, progress: 0, message: e.message });
      setGenerating(false);
    }
  };

  /* poll generation status */
  useEffect(() => {
    if (!jobId) return;
    const iv = setInterval(async () => {
      try {
        const res = await apiFetch(`${API_BASE}/generate/status/${jobId}`);
        if (!res.ok) return;
        const data = await res.json();
        setGenStatus(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(iv);
          setGenerating(false);
          if (data.status === 'completed') {
            setStep(3);
            loadOutputPreviews(jobId);
          }
        }
      } catch {}
    }, 1000);
    return () => clearInterval(iv);
  }, [jobId]);

  /* ── Step 3: Data check ── */
  const runDataValidation = async () => {
    if (!jobId) return;
    setDataValidating(true);
    try {
      const res = await apiFetch(`${API_BASE}/validate/${jobId}`);
      if (res.ok) setDataValidation(await res.json());
      else setDataValidation({ valid: false, message: 'Validation endpoint unavailable' });
    } catch {
      setDataValidation({ valid: true, message: 'Generation completed successfully' });
    }
    setDataValidating(false);
  };

  /* ── derived ── */
  const entities = configData?.entities || [];
  const totalFields = entities.reduce((s, e) => s + (e.fields?.length || 0), 0);
  const totalRels = entities.reduce((s, e) => s + (e.relationships?.length || 0), 0);
  const totalRows = entities.reduce((s, e) => s + (e.count || 0), 0);

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ts; }
  };

  /* can we move forward? */
  const canValidate = !!configData;
  const canGenerate = validation?.valid;
  const canDataCheck = genStatus?.status === 'completed';

  return (
    <div className="pl-page">
      {/* ── Hero ── */}
      <div className="pl-hero">
        <span className="hero-label">DATA GENERATION PIPELINE</span>
        <h1>Generate &amp; Validate</h1>
        <p>Select a saved config, validate the schema, generate data, and confirm integrity — all in one flow.</p>
      </div>

      {/* ── Step indicator ── */}
      <div className="pl-steps">
        {STEPS.map((s, i) => {
          const done = i < step;
          const active = i === step;
          const Icon = s.icon;
          return (
            <div key={s.key} className={`pl-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
              <div className="pl-step-icon"><Icon /></div>
              <span className="pl-step-label">{s.label}</span>
              {i < STEPS.length - 1 && <div className="pl-step-line" />}
            </div>
          );
        })}
      </div>

      <div className="pl-body">
        {/* ── Left: Config selector + ER preview ── */}
        <aside className="pl-sidebar">
          <div className="pl-sb-section">SAVED MODELS</div>
          {configs.length === 0 ? (
            <div className="pl-sb-empty">No configs found. Create one in Design Studio first.</div>
          ) : configs.map((cfg, i) => (
            <div
              key={cfg.name}
              className={`pl-sb-item ${selectedConfig?.name === cfg.name ? 'active' : ''}`}
              onClick={() => selectConfig(cfg)}
            >
              <div className="pl-sb-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />
              <div className="pl-sb-info">
                <span className="pl-sb-name">{cfg.name}</span>
                <span className="pl-sb-hint">{cfg.entities} entities · {formatDate(cfg.timestamp)}</span>
              </div>
            </div>
          ))}

          {/* ER diagram preview */}
          {configData && entities.length > 0 && (
            <div className="pl-er-preview">
              <div className="pl-sb-section" style={{ marginTop: 12 }}>ENTITY MODEL</div>
              <RelationshipGraph entities={entities} colors={DOT_COLORS} title={selectedConfig?.name || 'Config'} />
            </div>
          )}
        </aside>

        {/* ── Right: Active step content ── */}
        <div className="pl-main">
          {/* No config selected */}
          {!selectedConfig && (
            <div className="pl-empty-card">
              <IconFolder />
              <h3>Select a Config</h3>
              <p>Choose a saved configuration from the left panel to begin the pipeline.</p>
            </div>
          )}

          {/* Config selected — show summary + active step */}
          {selectedConfig && configData && (
            <>
              {/* Config summary strip */}
              <div className="pl-summary-strip">
                <div className="pl-sum-card">
                  <span className="pl-sum-label">Config</span>
                  <span className="pl-sum-value">{configData.name}</span>
                </div>
                <div className="pl-sum-card">
                  <span className="pl-sum-label">Entities</span>
                  <span className="pl-sum-value">{entities.length}</span>
                </div>
                <div className="pl-sum-card">
                  <span className="pl-sum-label">Fields</span>
                  <span className="pl-sum-value">{totalFields}</span>
                </div>
                <div className="pl-sum-card">
                  <span className="pl-sum-label">Relationships</span>
                  <span className="pl-sum-value">{totalRels}</span>
                </div>
                <div className="pl-sum-card">
                  <span className="pl-sum-label">Total rows</span>
                  <span className="pl-sum-value">{totalRows.toLocaleString()}</span>
                </div>
              </div>

              {/* ── STEP 0 & 1: Validate ── */}
              {step <= 1 && (
                <div className="pl-card">
                  <h2>Step 1 — Validate Schema</h2>
                  <p className="pl-card-desc">
                    Run the validation engine against your config to check entity definitions, field types, and relationship integrity before generating.
                  </p>

                  {/* Entity list */}
                  <div className="pl-entity-list">
                    {entities.map((ent, i) => (
                      <div key={i} className="pl-entity-chip">
                        <div className="pl-sb-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length], width: 6, height: 6 }} />
                        <span>{ent.name}</span>
                        <span className="pl-entity-meta">{ent.fields?.length || 0} fields · {ent.count.toLocaleString()} rows</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="pl-btn pl-btn-primary"
                    onClick={runValidation}
                    disabled={validating || !canValidate}
                  >
                    {validating ? 'Validating…' : 'Validate Config'}
                  </button>

                  {validation && (
                    <div className={`pl-result ${validation.valid ? 'pl-result-pass' : 'pl-result-fail'}`}>
                      <div className="pl-result-header">
                        {validation.valid ? '✓ Validation passed' : '✗ Validation failed'}
                      </div>
                      <div className="pl-result-stats">
                        <span>{validation.entity_count} entities</span>
                        <span>{validation.relationship_count} relationships</span>
                        <span>{validation.total_records?.toLocaleString()} records</span>
                      </div>
                      {validation.errors?.length > 0 && (
                        <ul className="pl-result-list pl-result-errors">
                          {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      )}
                      {validation.warnings?.length > 0 && (
                        <ul className="pl-result-list pl-result-warnings">
                          {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      )}
                      {validation.valid && (
                        <button className="pl-btn pl-btn-sm" onClick={() => setStep(2)} style={{ marginTop: 10 }}>
                          Continue to Generate →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 2: Generate ── */}
              {step === 2 && (
                <div className="pl-card">
                  <h2>Step 2 — Generate Data</h2>
                  <p className="pl-card-desc">
                    Configure generation options and start the data synthesis engine. Progress is tracked in real time.
                  </p>

                  <div className="pl-gen-options">
                    <div className="pl-gen-row">
                      <label>Dataset name <span style={{ color: 'var(--accent)', fontWeight: 600 }}>*</span></label>
                      <input
                        type="text"
                        value={genConfig.dataset_name}
                        onChange={(e) => setGenConfig({ ...genConfig, dataset_name: e.target.value })}
                        placeholder="e.g. Retail Q4 2026"
                        style={{ flex: 1 }}
                      />
                    </div>
                    <div className="pl-gen-row">
                      <label>Output format</label>
                      <select value={genConfig.output_format} onChange={(e) => setGenConfig({ ...genConfig, output_format: e.target.value })}>
                        <option value="csv">CSV</option>
                        <option value="json">JSON</option>
                        <option value="parquet">Parquet</option>
                      </select>
                    </div>
                    <div className="pl-gen-row" style={{ alignItems: 'flex-start' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Random seed
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          <svg
                            width="15" height="15" viewBox="0 0 15 15" fill="none"
                            style={{ cursor: 'pointer', color: '#888' }}
                            tabIndex={0}
                            onFocus={e => e.target.parentNode.querySelector('.pl-tooltip').style.opacity = 1}
                            onBlur={e => e.target.parentNode.querySelector('.pl-tooltip').style.opacity = 0}
                            onMouseEnter={e => e.currentTarget.parentNode.querySelector('.pl-tooltip').style.opacity = 1}
                            onMouseLeave={e => e.currentTarget.parentNode.querySelector('.pl-tooltip').style.opacity = 0}
                          >
                            <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
                            <text x="7.5" y="11" textAnchor="middle" fontSize="9" fill="currentColor">i</text>
                          </svg>
                          <span className="pl-tooltip" style={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            pointerEvents: 'none',
                            position: 'absolute',
                            left: '22px',
                            top: '-8px',
                            zIndex: 10,
                            background: '#222',
                            color: '#fff',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            maxWidth: '270px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.13)'
                          }}>
                            <b>Random seed</b> controls reproducibility.<br/>
                            Use the same value to get identical data each time.<br/>
                            <b>0</b>: system-random (different every run).<br/>
                            <b>100</b>: example of a user-chosen seed.<br/>
                            <b>Max:</b> 4,294,967,295 (2<sup>32</sup>-1).
                          </span>
                        </span>
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={genConfig.seed}
                        onChange={e => setGenConfig({ ...genConfig, seed: parseInt(e.target.value) })}
                        style={{ flex: 1, marginRight: 12 }}
                      />
                      <span style={{ minWidth: 32, display: 'inline-block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{genConfig.seed}</span>
                      {/* Helper text removed; see tooltip for details. */}
                    </div>
                    <div className="pl-gen-row pl-gen-check">
                      <label><input type="checkbox" checked={genConfig.parallel} onChange={(e) => setGenConfig({ ...genConfig, parallel: e.target.checked })} /> Parallel generation</label>
                    </div>
                    <div className="pl-gen-row pl-gen-check">
                      <label><input type="checkbox" checked={genConfig.validate_ri} onChange={(e) => setGenConfig({ ...genConfig, validate_ri: e.target.checked })} /> Validate referential integrity</label>
                    </div>
                    <div
                      className={`pl-ai-toggle${genConfig.enable_ai ? ' pl-ai-toggle--on' : ''}`}
                      onClick={() => setGenConfig({ ...genConfig, enable_ai: !genConfig.enable_ai })}
                    >
                      <span className="pl-ai-toggle-icon">✦</span>
                      <span className="pl-ai-toggle-label">
                        <strong>AI Enrichment</strong>
                        <span className="pl-ai-toggle-sub">Generate realistic values using Claude</span>
                      </span>
                      <span className={`pl-ai-toggle-pill${genConfig.enable_ai ? ' on' : ''}`}>
                        {genConfig.enable_ai ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  </div>


                  {/* Free plan: disable generation if 2+ datasets exist (use outputFolders, not outputPreviews) */}
                  {!genStatus && user?.plan === 'free' && outputFolders.length >= 2 ? (
                    <>
                      <span className="pl-btn pl-btn-primary pl-btn-disabled" style={{ opacity: 0.7, cursor: 'not-allowed', position: 'relative' }}
                        title="Free plan limit: Only 2 datasets allowed. Upgrade to Pro for more."
                        tabIndex={0}
                      >
                        Start Generation
                      </span>
                      <div
                        style={{
                          background: 'var(--color-background-secondary)',
                          borderRadius: 'var(--border-radius-lg)',
                          border: '0.5px solid var(--color-border-secondary)',
                          padding: '1rem 1.25rem',
                          margin: 0,
                          marginTop: 16
                        }}
                      >
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%' }}>
                              <path d="M12 2L2 20h20L12 2Z" fill="#FFD600" stroke="#FFD600" strokeWidth="2" />
                              <text x="12" y="17" textAnchor="middle" fontSize="12" fill="#7C6500" fontWeight="bold">!</text>
                            </svg>
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                              Dataset limit reached
                            </p>
                            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                              You have 2 free datasets. To continue, delete an existing dataset or upgrade to Pro for unlimited generation.
                            </p>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                              <button
                                style={{
                                  background: 'transparent',
                                  border: '0.5px solid var(--color-border-secondary)',
                                  borderRadius: 'var(--border-radius-md)',
                                  padding: '6px 12px',
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  color: 'var(--color-text-primary)',
                                  transition: 'background 0.2s'
                                }}
                                onClick={() => onNavigate && onNavigate('outputs')}
                              >
                                Manage datasets
                              </button>
                              <button
                                style={{
                                  background: 'transparent',
                                  border: '0.5px solid var(--color-border-info)',
                                  borderRadius: 'var(--border-radius-md)',
                                  padding: '6px 12px',
                                  fontSize: 13,
                                  cursor: 'pointer',
                                  color: 'var(--color-text-info)',
                                  transition: 'background 0.2s'
                                }}
                                onClick={() => window.open('https://your-pro-plan-link', '_blank')}
                              >
                                Learn about Pro ↗
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : !genStatus && (
                    <button className="pl-btn pl-btn-primary" onClick={startGeneration} disabled={generating || !genConfig.dataset_name.trim()}>
                      {generating ? 'Starting…' : 'Start Generation'}
                    </button>
                  )}

                  {genStatus && (
                    <div className="pl-gen-progress">
                      <div className="pl-progress-track">
                        <div className="pl-progress-fill" style={{ width: `${genStatus.progress || 0}%` }} />
                      </div>
                      <div className="pl-progress-info">
                        <span className={`pl-gen-status pl-gen-${genStatus.status}`}>{genStatus.status?.toUpperCase()}</span>
                        <span style={{ fontSize: 18, fontWeight: 600, color: '#6366f1' }}>{genStatus.progress || 0}%</span>
                        {genStatus.entity_name && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                            📄 {genStatus.entity_name}
                            {entities.length > 0 && <span style={{ color: '#6b7280', fontSize: '11px' }}>({Math.min(genStatus.message?.match(/\d+/)?.[0] || 1, entities.length)} of {entities.length})</span>}
                          </span>
                        )}
                        {genStatus.generated_records != null && <span style={{ fontSize: '12px', color: '#6b7280' }}>✨ {genStatus.generated_records.toLocaleString()} / {genStatus.total_records?.toLocaleString()}</span>}
                      </div>
                      <p className="pl-progress-msg">{genStatus.message}</p>
                      {genStatus.status === 'completed' && (
                        <>
                          {genStatus.errors?.length > 0 && (
                            <div className="pl-result pl-result-warn" style={{ marginTop: 10 }}>
                              <div className="pl-result-header">⚠ Completed with warnings</div>
                              {genStatus.errors.map((e, i) => <p key={i}>{e}</p>)}
                            </div>
                          )}
                          <button className="pl-btn pl-btn-sm" onClick={() => { setStep(3); loadOutputPreviews(jobId); }} style={{ marginTop: 10 }}>
                            Continue to Data Check →
                          </button>
                        </>
                      )}
                      {genStatus.status === 'failed' && (
                        <div className="pl-result pl-result-fail" style={{ marginTop: 10 }}>
                          <div className="pl-result-header">✗ Generation failed</div>
                          {genStatus.errors?.map((e, i) => <p key={i}>{e}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 3: Data Validation ── */}
              {step === 3 && (
                <div className="pl-card">
                  <h2>Step 3 — Data Validation</h2>
                  <p className="pl-card-desc">
                    Verify the generated data: check record counts, referential integrity, and field constraints.
                  </p>

                  <div className="pl-gen-progress" style={{ marginBottom: 16 }}>
                    <div className="pl-result pl-result-pass">
                      <div className="pl-result-header">✓ Generation completed</div>
                      <div className="pl-result-stats">
                        <span>{genStatus?.generated_records?.toLocaleString() || '—'} records generated</span>
                        <span>Job: {jobId?.substring(0, 8)}…</span>
                      </div>
                    </div>
                  </div>

                  {!dataValidation && (
                    <button className="pl-btn pl-btn-primary" onClick={runDataValidation} disabled={dataValidating}>
                      {dataValidating ? 'Validating data…' : 'Run Data Validation'}
                    </button>
                  )}

                  {dataValidation && (
                    <div className={`pl-result ${dataValidation.valid !== false ? 'pl-result-pass' : 'pl-result-fail'}`}>
                      <div className="pl-result-header">
                        {dataValidation.valid !== false ? '✓ Data validation passed' : '✗ Data validation failed'}
                      </div>
                      {dataValidation.checks && Object.entries(dataValidation.checks).map(([k, v]) => (
                        <DataCheckRow key={k} name={k} check={v} />
                      ))}
                    </div>
                  )}

                    <div className="pl-final-actions">
                    <button className="pl-btn pl-btn-sm" onClick={() => onNavigate('outputs')}>
                      View all outputs →
                    </button>
                    <button className="pl-btn pl-btn-outline pl-btn-sm" onClick={() => { setStep(0); setSelectedConfig(null); setConfigData(null); setValidation(null); setGenStatus(null); setJobId(null); setDataValidation(null); setOutputPreviews([]); setPreviewTab(0); }}>
                      Start new pipeline
                    </button>
                  </div>

                  {/* ── Inline data preview ── */}
                  {previewLoading && <p style={{ marginTop: 16, color: 'var(--muted)' }}>Loading data preview…</p>}
                  {outputPreviews.length > 0 && (
                    <div className="pl-preview-section">
                      <div className="pl-sb-section" style={{ marginBottom: 10 }}>DATA PREVIEW</div>
                      <div className="pl-preview-tabs">
                        {outputPreviews.map(({ name }, i) => (
                          <button
                            key={name}
                            className={`pl-preview-tab${previewTab === i ? ' active' : ''}`}
                            onClick={() => setPreviewTab(i)}
                          >{name}</button>
                        ))}
                      </div>
                      {(() => {
                        const p = outputPreviews[previewTab];
                        if (!p) return null;
                        return (
                          <div className="pl-preview-table-wrap">
                            <div style={{ overflowX: 'auto' }}>
                              <table className="pl-preview-table">
                                <thead>
                                  <tr>{p.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                  {p.rows.slice(0, 5).map((row, ri) => (
                                    <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Pipeline;